import 'ol/ol.css';
import type FeatureLike from 'ol/Feature';
import GeoJSONFormat from 'ol/format/GeoJSON';
import Map from 'ol/Map';
import { fromLonLat } from 'ol/proj';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import type { StyleLike } from 'ol/style/Style';
import View from 'ol/View';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MapBrandWatermark } from '../components/map/MapBrandWatermark';
import { BasemapIcon, LegendIcon, ZoomInIcon, ZoomOutIcon } from '../components/icons/MapToolIcons';
import { ApiError } from '../lib/api';
import { publicApi, toAdminBasemap, type PublicLayerDto, type PublicMapDto } from '../lib/publicApi';
import type { AdminBasemap } from '../admin/types';
import { buildBasemapLayer, createFallbackBasemapLayer, pickInitialBasemap } from '../map/basemaps';
import type { LayerConfig } from '../map/demoLayers';
import { resolveLayerStyle } from '../map/styleResolve';

type LoadedLayer = { config: LayerConfig; source: PublicLayerDto };

/** The public, unauthenticated map viewer behind the "Доступ" tab's embed
 * links — deliberately read-only and deliberately narrower than the real
 * map: no search (search-service has no public endpoint), no editing, no
 * object cards, no icon markers (media.files always requires a JWT), vector
 * layers only (no WMS/WMTS/raster — see public.py). Pan/zoom/legend/basemap
 * switch is the whole feature set, matching what "embed this map on another
 * site" actually needs. */
export function EmbedMapPage() {
  const { token } = useParams<{ token: string }>();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Map | null>(null);

  const [mapInfo, setMapInfo] = useState<PublicMapDto | null>(null);
  const [basemaps, setBasemaps] = useState<AdminBasemap[]>([]);
  const [layers, setLayers] = useState<LoadedLayer[] | null>(null);
  const [activeBasemapId, setActiveBasemapId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [legendOpen, setLegendOpen] = useState(false);
  const [basemapPanelOpen, setBasemapPanelOpen] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const [map, basemapDtos, layerDtos] = await Promise.all([
          publicApi.getMap(token),
          publicApi.listBasemaps(token),
          publicApi.listLayers(token),
        ]);
        const loaded = await Promise.all(
          layerDtos.map(async (dto): Promise<LoadedLayer> => {
            const fc = await publicApi.getLayerFeatures(token, dto.id);
            return {
              source: dto,
              config: {
                id: dto.id,
                name: dto.name,
                color: dto.style_color ?? '#2563eb',
                opacity: dto.style_opacity ?? 1,
                count: fc.features.length,
                defaultVisible: true,
                geojson: fc as LayerConfig['geojson'],
                geometryType: dto.geometry_type,
                markerMode: 'color', // icon markers need an authenticated media.files fetch — not available here
                styleType: dto.style_type,
                styleRules: dto.style_rules,
                styleFallback: dto.style_fallback ?? undefined,
              },
            };
          }),
        );
        if (cancelled) return;
        setMapInfo(map);
        setBasemaps(basemapDtos.map(toAdminBasemap));
        setLayers(loaded);
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof ApiError ? err.message : 'Не удалось загрузить карту');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!mapInfo || !layers || !containerRef.current || mapRef.current) return;

    const initialBasemap = pickInitialBasemap(basemaps, mapInfo.default_basemap_name);
    setActiveBasemapId(initialBasemap?.id ?? null);
    const baseLayer = initialBasemap ? buildBasemapLayer(initialBasemap) : createFallbackBasemapLayer();
    const format = new GeoJSONFormat();

    const vectorLayers = layers.map(({ config }) => {
      const source = new VectorSource({ features: format.readFeatures(config.geojson, { featureProjection: 'EPSG:3857' }) });
      return new VectorLayer({
        source,
        style: ((feature: FeatureLike) => resolveLayerStyle(config, feature, false)) as unknown as StyleLike,
        opacity: config.opacity ?? 1,
      });
    });

    const extent = mapInfo.restrict_pan_to_extent
      ? [...fromLonLat([mapInfo.min_lon, mapInfo.min_lat]), ...fromLonLat([mapInfo.max_lon, mapInfo.max_lat])]
      : undefined;

    const view = new View({
      center: fromLonLat([mapInfo.center_lon, mapInfo.center_lat]),
      zoom: mapInfo.zoom,
      minZoom: Math.max(0, mapInfo.zoom_min),
      maxZoom: mapInfo.zoom_max,
      extent: extent as [number, number, number, number] | undefined,
      // Only keeps the center point inside the bbox — the plain (default)
      // extent constraint instead keeps the whole *viewport* inside it, which
      // silently overrides the configured zoom/center to whatever fits when
      // the bbox is smaller than what that zoom would show (a small "Начальный
      // вид и территория" box combined with a wide zoom is a normal
      // combination, not a misconfiguration to fight).
      constrainOnlyCenter: true,
    });

    const map = new Map({
      target: containerRef.current,
      view,
      layers: [baseLayer, ...vectorLayers],
      controls: [],
    });
    mapRef.current = map;

    // The container's real size can settle after this effect runs (e.g. the
    // "Доступ" tab's scaled-down preview, or any layout pass after iframe
    // mount) — OL's own ResizeObserver handles ongoing changes, but the very
    // first paint can still race it, so force one explicit recompute.
    requestAnimationFrame(() => map.updateSize());

    return () => {
      map.setTarget(undefined);
      mapRef.current = null;
    };
  }, [mapInfo, layers, basemaps]);

  const handleBasemapChange = (id: string) => {
    const found = basemaps.find((b) => b.id === id);
    const map = mapRef.current;
    if (!found || !map) return;
    map.getLayers().setAt(0, buildBasemapLayer(found));
    setActiveBasemapId(id);
    setBasemapPanelOpen(false);
  };

  if (loadError) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-bg-canvas p-6 text-center">
        <p className="text-sm font-medium text-status-danger">{loadError}</p>
      </div>
    );
  }

  if (!mapInfo || !layers) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-bg-canvas">
        <p className="text-sm text-text-secondary">Загрузка карты…</p>
      </div>
    );
  }

  return (
    <div className="relative h-screen w-full overflow-hidden bg-bg-canvas">
      <div ref={containerRef} className="absolute inset-0" />
      <MapBrandWatermark />

      <div className="absolute right-4 top-4 z-10 flex flex-col gap-2">
        <button
          type="button"
          onClick={() => mapRef.current?.getView().animate({ zoom: (mapRef.current.getView().getZoom() ?? 13) + 1, duration: 200 })}
          className="flex size-9 items-center justify-center rounded-lg border border-border-default bg-bg-surface text-text-primary shadow-sm hover:bg-bg-surface-secondary"
        >
          <ZoomInIcon className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => mapRef.current?.getView().animate({ zoom: (mapRef.current.getView().getZoom() ?? 13) - 1, duration: 200 })}
          className="flex size-9 items-center justify-center rounded-lg border border-border-default bg-bg-surface text-text-primary shadow-sm hover:bg-bg-surface-secondary"
        >
          <ZoomOutIcon className="size-4" />
        </button>

        {mapInfo.embed_config.show_basemap_switcher && basemaps.length > 1 && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setBasemapPanelOpen((v) => !v)}
              className="flex size-9 items-center justify-center rounded-lg border border-border-default bg-bg-surface text-text-primary shadow-sm hover:bg-bg-surface-secondary"
            >
              <BasemapIcon className="size-4" />
            </button>
            {basemapPanelOpen && (
              <div className="absolute right-11 top-0 flex w-48 flex-col gap-1 rounded-xl border border-border-default bg-bg-surface p-2 shadow-lg">
                {basemaps.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => handleBasemapChange(b.id)}
                    className={`truncate rounded-lg px-3 py-2 text-left text-xs font-medium ${
                      activeBasemapId === b.id ? 'bg-action-primary text-text-inverse' : 'text-text-primary hover:bg-bg-surface-secondary'
                    }`}
                  >
                    {b.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {mapInfo.embed_config.show_legend && layers.length > 0 && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setLegendOpen((v) => !v)}
              className="flex size-9 items-center justify-center rounded-lg border border-border-default bg-bg-surface text-text-primary shadow-sm hover:bg-bg-surface-secondary"
            >
              <LegendIcon className="size-4" />
            </button>
            {legendOpen && (
              <div className="absolute right-11 top-0 flex w-56 flex-col gap-2 rounded-xl border border-border-default bg-bg-surface p-3 shadow-lg">
                {layers.map(({ config }) => (
                  <div key={config.id} className="flex items-center gap-2">
                    <span className="size-3 shrink-0 rounded-full" style={{ backgroundColor: config.color }} />
                    <span className="truncate text-[11px] text-text-primary">{config.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
